import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button, InputTextArea, Text } from "@pentabd/ui";

import {
  Comments,
  ErrorModal,
  MultiFile,
  SectionTitle,
  StatusBadge,
  SuccessModal,
} from "@components";

import { FORM_FIELDS } from "@constants/form";
import { STATUS } from "@constants/status.hrm";

import { addTicketComment } from "@api/e-tickets/ticket";

import { TicketCommentsType } from "@type/tickets/ticket-types";
import { customPriorityBadgeColor } from "@utils/customPriorityBadgeColor";

const { SUCCESS, FAILED } = STATUS;
const COMMENT = FORM_FIELDS.E_TICKET.COMMENT;

export const TicketDetailsComment = ({
  data,
  ticketDropdown,
}: {
  data: any;
  ticketDropdown: any;
}) => {
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);

  const { t } = useTranslation();

  const { data: ticketData, handleAddNewComment } = data || {};
  const { categories, priorities } = ticketDropdown;

  const {
    handleSubmit,
    register,
    control,
    setValue,
    reset,
    formState: { errors },
  } = useForm<TicketCommentsType>();

  // error and success modal
  const closeErrorModal = () => setIsErrorModalOpen(false);
  const successAction = () => {
    reset();
    setIsSuccessModalOpen(false);
  };

  useEffect(() => {
    const reset = () => {
      setIsErrorModalOpen(false);
      setIsSuccessModalOpen(false);
    };
    return reset();
  }, []);

  const onSubmit: SubmitHandler<TicketCommentsType> = (
    data: TicketCommentsType,
  ) => {
    addTicketComment(
      ticketData.id,
      {
        comment: data.comment,
        document: data.document,
      },
      (response) => {
        closeErrorModal();
        if (response.status === SUCCESS) {
          setIsSuccessModalOpen(true);
          handleAddNewComment(response.data);
        } else if (response.status === FAILED) setIsErrorModalOpen(true);
      },
    );
  };

  return (
    <>
      {/* MODAL SECTION */}
      <>
        <SuccessModal
          isModalOpen={isSuccessModalOpen}
          action={successAction}
          subtitle="Ticket Updated Successfully"
        />
        <ErrorModal
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
        />
      </>
      {/* MODAL SECTION */}
      <SectionTitle label="TICKETS.DETAILS.FORM.COMMENT.LABEL" size="lg" />
      {/* showing comment */}
      {ticketData?.comments?.length > 0 && (
        <div className="border-bottom pb-10 mb-10">
          <Comments data={ticketData?.comments} />
        </div>
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="row">
          <div className="col-3" />
          <div className="col-lg-7">
            <Text weight="semibold" color="title" size="sm" component="p">
              {ticketData?.subject}
            </Text>
            <div className="d-flex gap-8 py-5 mb-8 border-bottom">
              <StatusBadge status={ticketData?.status} />
              <StatusBadge
                status={
                  priorities?.find(
                    (item: any) => item.id === ticketData?.priority,
                  )?.name
                }
                style={customPriorityBadgeColor(
                  priorities,
                  ticketData?.priority,
                )}
              />
              <StatusBadge
                status={
                  categories?.find(
                    (item: any) => item.id === ticketData?.category,
                  )?.name
                }
                style={{ background: "#fbebff" }} // random color
              />
            </div>
            <InputTextArea
              {...register(COMMENT.RICH_TEXT_EDITOR, {
                required: "Comment Text is Required",
              })}
              placeholder={t(
                "TICKETS.DETAILS.FORM.COMMENT.COMMENT.PLACEHOLDER",
              )}
              error={errors as any}
            />
            <div className="mt-8">
              <Text
                weight="semibold"
                color="title"
                size="sm"
                component="p"
                className="mb-3"
              >
                {t("TICKETS.DETAILS.FORM.COMMENT.DOCUMENTS.LABEL")}
              </Text>
              <MultiFile
                id={COMMENT.COMMENT_DOC}
                control={control}
                setValue={setValue}
                register={register}
                errors={errors}
              />
            </div>
            <div className="d-flex justify-content-end mt-10">
              <Button fill="fill" type="primary" size="sm" htmlType="submit">
                {t("TICKETS.DETAILS.FORM.COMMENT.SUBMIT_COMMENT")}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};
